import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingSpinner, ErrorMessage } from './CommonUI';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { profilePictureUrl } from '../utils/storageUrls';
import DOMPurify from 'dompurify';
import AdSenseAd from './AdSenseAd';

const AD_BREAK_MARKER = '<div class="adsense-in-article-break"></div>';

/**
 * Splits sanitized article HTML on ad break markers and returns an array
 * of alternating content-chunk / ad-unit elements for React rendering.
 */
function renderArticleWithAds(html) {
  const parts = html.split(AD_BREAK_MARKER);
  if (parts.length === 1) {
    return (
      <div
        className="blog-article-body prose prose-lg max-w-none prose-p:text-gray-700 prose-p:leading-relaxed prose-strong:text-gray-900 prose-ul:text-gray-700 prose-ol:text-gray-700 prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-code:text-slate-900 prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return parts.map((chunk, i) => (
    <React.Fragment key={i}>
      <div
        className="blog-article-body prose prose-lg max-w-none prose-p:text-gray-700 prose-p:leading-relaxed prose-strong:text-gray-900 prose-ul:text-gray-700 prose-ol:text-gray-700 prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-code:text-slate-900 prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded"
        dangerouslySetInnerHTML={{ __html: chunk }}
      />
      {i < parts.length - 1 && <AdSenseAd type="in-article" className="my-4" />}
    </React.Fragment>
  ));
}

const BlogPost = () => {
    const { slug } = useParams();
    const { user, checkAdminStatus } = useAuth();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isAdminPreview, setIsAdminPreview] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    const documentTitle = useMemo(() => {
        if (post?.meta_title) return post.meta_title;
        if (post?.title) return post.title;
        if (error) return 'Blog';
        return 'Blog';
    }, [post, error]);

    const metaDescription = useMemo(() => {
        if (post?.meta_description) return post.meta_description;
        if (post?.excerpt) return post.excerpt;
        return 'Read the latest MyBrickLog blog posts with LEGO® news, tips, and collection insights.';
    }, [post]);

    const canonicalUrl = useMemo(() => {
        if (!post?.slug) return null;
        return `https://www.mybricklog.com/blog/${post.slug}`;
    }, [post]);

    const ogImage = useMemo(() => {
        if (post?.featured_image) return post.featured_image;
        return 'https://www.mybricklog.com/thumbnail.png';
    }, [post]);

    useDocumentTitle(documentTitle, metaDescription, {
        canonicalUrl,
        ogType: 'article',
        ogImage
    });

    useEffect(() => {
        fetchBlogPost();
    }, [slug]);

    // Non-blocking admin check
    useEffect(() => {
        const checkAdmin = async () => {
            if (user) {
                try {
                    const adminStatus = await checkAdminStatus();
                    setIsAdmin(adminStatus);
                } catch (error) {
                    console.error('Error checking admin status:', error);
                    setIsAdmin(false);
                }
            } else {
                setIsAdmin(false);
            }
        };
        
        checkAdmin();
    }, [user, checkAdminStatus]);

    const fetchBlogPost = async () => {
        setIsLoading(true);
        try {
            // Fetch post data
            const postResponse = await fetch(`${process.env.REACT_APP_API_URL}/blog_get_post.php?slug=${encodeURIComponent(slug)}`, {
                credentials: 'include'
            });
            
            if (!postResponse.ok) {
                if (postResponse.status === 404) {
                    setError('Blog post not found.');
                } else {
                    setError('Unable to load blog post. Please try again later.');
                }
                setIsLoading(false);
                return;
            }

            const postData = await postResponse.json();

            if (!postData.success) {
                setError(postData.message || 'Unable to load blog post.');
                setIsLoading(false);
                return;
            }

            setPost(postData.post);
            setRelatedPosts(postData.related_posts || []);
            setIsAdminPreview(!!postData.is_admin_preview);

            // Fetch comments for this post
            const commentsResponse = await fetch(`${process.env.REACT_APP_API_URL}/blog_get_comments.php?post_id=${postData.post.id}`);
            
            if (commentsResponse.ok) {
                const commentsData = await commentsResponse.json();
                if (commentsData.success) {
                    setComments(commentsData.comments);
                }
            }
            
        } catch (error) {
            console.error('Error fetching blog post:', error);
            setError('Unable to load blog post. Please try again later.');
        }
        setIsLoading(false);
    };

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!user) {
            navigate('/login');
            return;
        }

        if (!newComment.trim()) return;

        setIsSubmittingComment(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/blog_add_comment.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    post_id: post.id,
                    content: newComment,
                    parent_id: replyTo
                })
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to submit comment');
            }

            // Add the new comment to the UI
            const newCommentData = data.comment;

            if (replyTo) {
                // Add as reply
                setComments(prev => prev.map(comment => {
                    if (comment.id === replyTo) {
                        return {
                            ...comment,
                            replies: [...comment.replies, newCommentData]
                        };
                    }
                    return comment;
                }));
                setReplyTo(null);
            } else {
                // Add as top-level comment with empty replies array
                setComments(prev => [...prev, { ...newCommentData, replies: [] }]);
            }

            setNewComment('');
        } catch (error) {
            console.error('Error submitting comment:', error);
            setError('Failed to submit comment. Please try again.');
        }
        setIsSubmittingComment(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not published yet';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateShort = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatReadTime = (content = '') => {
        const wordsPerMinute = 200;
        const wordCount = content.replace(/<[^>]*>/g, '').split(' ').length;
        const readTime = Math.ceil(wordCount / wordsPerMinute);
        return `${readTime} min read`;
    };

    const getShareUrl = () => canonicalUrl || window.location.href;

    const handleShareX = () => {
        const url = encodeURIComponent(getShareUrl());
        const text = encodeURIComponent(post?.title || '');
        window.open(
            `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
            '_blank',
            'noopener,noreferrer,width=550,height=420'
        );
    };

    const handleShareFacebook = () => {
        const url = encodeURIComponent(getShareUrl());
        window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${url}`,
            '_blank',
            'noopener,noreferrer,width=550,height=420'
        );
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(getShareUrl());
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2500);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <LoadingSpinner text="Loading blog post..." size="lg" />
                </div>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <ErrorMessage 
                        message={error || 'Blog post not found.'} 
                        onRetry={() => fetchBlogPost()} 
                    />
                    <div className="mt-6 text-center">
                        <Link 
                            to="/blog" 
                            className="text-red-600 hover:text-red-800 font-medium"
                        >
                            <FontAwesomeIcon icon="arrow-left" className="mr-2" />
                            Back to Blog
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const sanitizedHtml = DOMPurify.sanitize(post.content || '', {
        USE_PROFILES: { html: true },
        ADD_ATTR: ['target', 'rel', 'loading'],
        FORCE_BODY: false,
    });

    const articleSchema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.meta_title || post.title,
        description: metaDescription,
        image: ogImage,
        author: {
            '@type': 'Person',
            name: post.author?.username || 'MyBrickLog Team'
        },
        publisher: {
            '@type': 'Organization',
            name: 'MyBrickLog',
            logo: {
                '@type': 'ImageObject',
                url: 'https://www.mybricklog.com/logo192.png'
            }
        },
        datePublished: post.published_at || post.created_at,
        dateModified: post.updated_at || post.published_at || post.created_at,
        mainEntityOfPage: canonicalUrl || `https://www.mybricklog.com/blog/${slug}`
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
            />

            {/* Breadcrumb */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                    <nav className="flex items-center gap-2 text-sm text-gray-500">
                        <Link to="/" className="hover:text-gray-800 transition-colors">Home</Link>
                        <FontAwesomeIcon icon="chevron-right" className="text-gray-300 text-xs" />
                        <Link to="/blog" className="hover:text-gray-800 transition-colors">Blog</Link>
                        <FontAwesomeIcon icon="chevron-right" className="text-gray-300 text-xs" />
                        <span className="text-gray-800 truncate">{post.title}</span>
                    </nav>
                </div>
            </div>

            <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

                {/* ── Article Header ── */}
                <header className="mb-8">
                    {isAdminPreview && (
                        <div className="mb-6 rounded border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3">
                            <div className="flex items-start gap-3">
                                <FontAwesomeIcon icon="circle-exclamation" className="mt-0.5" />
                                <div>
                                    <p className="font-semibold">Draft Preview (Admin Only)</p>
                                    <p className="text-sm">This post is in draft status and is only visible to admins.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Category badge — sharp, all-caps, solid red */}
                    <div className="mb-4">
                        <Link
                            to={`/blog?category=${post.category.slug}`}
                            className="blog-category-badge"
                        >
                            {post.category.name}
                        </Link>
                    </div>

                    {/* Title — large display font */}
                    <h1 className="blog-display-title text-gray-900 mb-5">
                        {post.title}
                    </h1>

                    {/* Meta row — red left-border accent */}
                    <div className="blog-meta-accent flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-500 mb-5">
                        <span>
                            <FontAwesomeIcon icon="user" className="mr-1.5 text-gray-400" />
                            By {post.author.username}
                        </span>
                        <span className="text-gray-300 hidden sm:inline">·</span>
                        <span>
                            <FontAwesomeIcon icon="calendar" className="mr-1.5 text-gray-400" />
                            {formatDateShort(post.published_at || post.created_at)}
                        </span>
                        <span className="text-gray-300 hidden sm:inline">·</span>
                        <span>
                            <FontAwesomeIcon icon="clock" className="mr-1.5 text-gray-400" />
                            {formatReadTime(post.content)}
                        </span>
                        <span className="text-gray-300 hidden sm:inline">·</span>
                        <span>
                            <FontAwesomeIcon icon="eye" className="mr-1.5 text-gray-400" />
                            {post.view_count} views
                        </span>
                        {isAdmin && (
                            <Link
                                to={`/admin/blog/edit/${post.id}`}
                                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 bg-red-600 hover:bg-red-700 text-white hover:text-white rounded text-xs font-semibold transition-colors"
                            >
                                <FontAwesomeIcon icon="edit" />
                                Edit Post
                            </Link>
                        )}
                    </div>

                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {post.tags.map(tag => (
                                <span key={tag.id} className="blog-tag">
                                    {tag.name}
                                </span>
                            ))}
                        </div>
                    )}
                </header>

                {/* ── Lede / Excerpt ── */}
                {post.excerpt && (
                    <div className="blog-lede mb-10">
                        {post.excerpt}
                    </div>
                )}

                {/* ── Featured Image ── */}
                {post.featured_image && (
                    <figure className="mb-10">
                        <img
                            src={post.featured_image}
                            alt={post.title}
                            className="block w-full h-auto rounded-md"
                            onError={(e) => { e.target.src = '/images/lego_piece_questionmark.png'; }}
                        />
                    </figure>
                )}

                {/* ── In-article ad (auto-placed before body) ── */}
                <AdSenseAd type="in-article" className="mb-8" />

                {/* ── Article Body ── */}
                <div className="bg-white border border-gray-100 rounded p-3 sm:p-6 lg:p-8 mb-8 shadow-sm">
                    {renderArticleWithAds(sanitizedHtml)}
                </div>

                {/* ── Share ── */}
                <div className="bg-white border border-gray-100 rounded p-6 mb-8 shadow-sm">
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Share This Post</p>
                    <div className="flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={handleShareX}
                            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded text-gray-600 hover:border-gray-800 hover:text-gray-900 transition-colors"
                            aria-label="Share on X"
                        >
                            <FontAwesomeIcon icon={['fab', 'x-twitter']} />
                            X
                        </button>
                        <button
                            type="button"
                            onClick={handleShareFacebook}
                            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded text-gray-600 hover:border-blue-700 hover:text-blue-800 transition-colors"
                            aria-label="Share on Facebook"
                        >
                            <FontAwesomeIcon icon={['fab', 'facebook']} />
                            Facebook
                        </button>
                        <button
                            type="button"
                            onClick={handleCopyLink}
                            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded text-gray-600 hover:border-gray-400 transition-colors"
                            aria-label="Copy link to clipboard"
                        >
                            <FontAwesomeIcon icon={linkCopied ? 'check' : 'link'} />
                            {linkCopied ? 'Copied!' : 'Copy Link'}
                        </button>
                    </div>
                </div>

                {/* ── Comments ── */}
                <div className="bg-white border border-gray-100 rounded p-6 mb-8 shadow-sm">
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-6">
                        Comments ({comments.reduce((total, comment) => total + 1 + comment.replies.length, 0)})
                    </p>

                    {user ? (
                        <form onSubmit={handleCommentSubmit} className="mb-8">
                            {replyTo && (
                                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Replying to comment</span>
                                    <button type="button" onClick={() => setReplyTo(null)} className="text-gray-400 hover:text-gray-600">
                                        <FontAwesomeIcon icon="times" />
                                    </button>
                                </div>
                            )}
                            <div className="flex gap-4">
                                <img
                                    src={profilePictureUrl(user.profile_picture) || '/images/lego_user.png'}
                                    alt={user.username}
                                    className="w-10 h-10 rounded-full flex-shrink-0"
                                />
                                <div className="flex-1">
                                    <textarea
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        placeholder="Write a comment..."
                                        className="w-full p-3 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
                                        rows="3"
                                        required
                                    />
                                    <div className="mt-3 flex justify-end">
                                        <button
                                            type="submit"
                                            disabled={isSubmittingComment || !newComment.trim()}
                                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-semibold"
                                        >
                                            {isSubmittingComment ? (
                                                <>
                                                    <FontAwesomeIcon icon="spinner" className="animate-spin mr-2" />
                                                    Posting...
                                                </>
                                            ) : (
                                                replyTo ? 'Post Reply' : 'Post Comment'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    ) : (
                        <div className="mb-8 p-4 bg-gray-50 border border-gray-100 rounded text-center">
                            <p className="text-sm text-gray-600 mb-3">Sign in to join the conversation.</p>
                            <Link
                                to="/login"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white hover:text-white rounded hover:bg-red-700 transition-colors text-sm font-semibold"
                            >
                                <FontAwesomeIcon icon="user" />
                                Sign In to Comment
                            </Link>
                        </div>
                    )}

                    <div className="space-y-6">
                        {comments.map(comment => (
                            <div key={comment.id} className="border-b border-gray-100 pb-6 last:border-b-0 last:pb-0">
                                <div className="flex gap-3">
                                    <img
                                        src={'/images/lego_user.png'}
                                        alt={comment.author.username}
                                        className="w-9 h-9 rounded-full flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1.5">
                                            <span className="font-semibold text-gray-900 text-sm">{comment.author.username}</span>
                                            <span className="text-xs text-gray-400">{formatDate(comment.created_at)}</span>
                                        </div>
                                        <p className="text-sm text-gray-700 leading-relaxed mb-2 break-words">{comment.content}</p>
                                        {user && (
                                            <button
                                                onClick={() => setReplyTo(comment.id)}
                                                className="text-xs font-bold uppercase tracking-wide text-red-600 hover:text-red-800"
                                            >
                                                <FontAwesomeIcon icon="reply" className="mr-1" />
                                                Reply
                                            </button>
                                        )}
                                        {comment.replies && comment.replies.length > 0 && (
                                            <div className="mt-4 pl-5 border-l-2 border-gray-100 space-y-4">
                                                {comment.replies.map(reply => (
                                                    <div key={reply.id} className="flex gap-3">
                                                        <img
                                                            src={'/images/lego_user.png'}
                                                            alt={reply.author.username}
                                                            className="w-7 h-7 rounded-full flex-shrink-0"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1">
                                                                <span className="font-semibold text-gray-900 text-xs">{reply.author.username}</span>
                                                                <span className="text-xs text-gray-400">{formatDate(reply.created_at)}</span>
                                                            </div>
                                                            <p className="text-sm text-gray-700 break-words">{reply.content}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Related Posts ── */}
                {relatedPosts.length > 0 && (
                    <div className="bg-white border border-gray-100 rounded p-6 mb-8 shadow-sm">
                        <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-6">Related Posts</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {relatedPosts.map(relatedPost => (
                                <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`} className="group block">
                                    <div className="aspect-video mb-3 overflow-hidden rounded bg-gray-100">
                                        <img
                                            src={relatedPost.featured_image || '/images/lego_piece_questionmark.png'}
                                            alt={relatedPost.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                    </div>
                                    <h4 className="font-semibold text-sm text-gray-900 group-hover:text-red-600 transition-colors line-clamp-2">
                                        {relatedPost.title}
                                    </h4>
                                    <p className="text-xs text-gray-400 mt-1">{formatDateShort(relatedPost.published_at)}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

            </article>

            {/* Back to Blog */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                <Link
                    to="/blog"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-red-600 transition-colors"
                >
                    <FontAwesomeIcon icon="arrow-left" />
                    Back to Blog
                </Link>
            </div>
        </div>
    );
};

export default BlogPost;
